import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    ViewContainerRef,
    inject
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgClass } from '@angular/common';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ScrollService } from '../../../core/common/services/scroll.service';
import { DialogStyle } from '../../../elements/components/dialog/entities/dialog.entity';
import { ShIconComponent } from '../../../elements/components/icon/icon.component';
import { EscapeDialogDirective } from '../../../elements/directives/escape-dialog/escape-dialog.directive';
import { DialogInsertionDirective } from '../../directives/dynamic-dialog-insertion.directive';
import { DialogConfig } from '../../entities/dialog-config.entity';

@Component({
    selector: 'sh-dynamic-dialog',
    templateUrl: './dynamic-dialog.component.html',
    standalone: true,
    imports: [EscapeDialogDirective, NgClass, DialogInsertionDirective, ShIconComponent, AsyncPipe]
})
export class ShDynamicDialogComponent extends BaseContainer implements AfterViewInit, OnInit, OnDestroy {
    private readonly onCloseSubject$ = new Subject<void>();

    public readonly dialogStyle$: BehaviorSubject<DialogStyle> = new BehaviorSubject<DialogStyle>(DialogStyle.Normal);

    public componentRef!: ComponentRef<any>;
    public childComponentType?: Type<any>;
    public onClose$: Observable<any> = this.onCloseSubject$.asObservable();

    @ViewChild(DialogInsertionDirective, { static: true }) private readonly insertionPoint!: DialogInsertionDirective;
    @ViewChild('dynamicDialog', { read: ElementRef, static: true }) private readonly dialogEl!: ElementRef;

    // Injectors
    private readonly scrollService: ScrollService = inject(ScrollService);
    private readonly element: ElementRef = inject(ElementRef);

    constructor(
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        public dialogConfig: DialogConfig
    ) {
        super();
    }

    ngOnInit(): void {
        this.setDialogStyle();
        this.closeOnNavigationEnd();
        this.openDialog();
    }

    ngAfterViewInit(): void {
        if (this.childComponentType) {
            this.loadChildComponent(this.childComponentType);
        }

        this.cd.detectChanges();

        const scrollContainer: Element | Window =
            this.element.nativeElement.querySelector('.sh-dialog__content') || window;

        scrollContainer.addEventListener('scroll', () => {
            this.scrollService.triggerScrollEvent();
        });
    }

    ngOnDestroy(): void {
        this.onCloseSubject$.complete();

        this.destroy$.next();
        this.destroy$.complete();

        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    closeDialog(): void {
        if (this.nativeElement.close) {
            this.nativeElement.close();
        } else {
            this.nativeElement.removeAttribute('open');
        }
        this.onCloseSubject$.next();
    }

    openDialog(): void {
        if (this.nativeElement.showModal) {
            try {
                this.nativeElement.showModal();
            } catch (error) {
                console.error('ShDynamicDialogComponent:: Error opening dialog', error);
                this.closeDialog();
            }
        } else {
            this.nativeElement.setAttribute('open', 'true');
        }
    }

    setDialogStyle(): void {
        if (this.dialogConfig.options && this.dialogConfig.options.style) {
            this.dialogStyle$.next(this.dialogConfig.options.style);
        }
    }

    private loadChildComponent(componentType: Type<any>): void {
        const componentFactory: ComponentFactory<any> =
            this.componentFactoryResolver.resolveComponentFactory(componentType);

        const viewContainerRef: ViewContainerRef = this.insertionPoint.viewContainerRef;
        viewContainerRef.clear();

        this.componentRef = viewContainerRef.createComponent(componentFactory);
    }

    private closeOnNavigationEnd(): void {
        if (this.dialogConfig.options?.canCloseDialogOnNavigationEvent === false) {
            return;
        }

        const navigationEndEvents$: Observable<Event> = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        );

        navigationEndEvents$.pipe(takeUntil(this.destroy$)).subscribe(() => this.closeDialog());
    }

    get nativeElement(): HTMLDialogElement {
        return this.dialogEl.nativeElement as HTMLDialogElement;
    }
}
